import { Link } from "react-router-dom";
import { CalendarCheck, FileSignature, ShieldCheck, Phone, Mail, MapPin, ArrowRight, Clock } from "lucide-react";
import { COMPANY, US_STATES } from "../constants/company";
import { USMap } from "../components/USMap";
import { AIChatWidget } from "../components/AIChatWidget";
import { Button } from "../components/ui/button";

const services = [
  { icon: FileSignature, title: "Individual & Business Returns", text: "Accurate federal and state preparation for W-2 earners, 1099 contractors, and small businesses." },
  { icon: CalendarCheck, title: "Year-Round Planning", text: "Book a session any time — we help you plan ahead instead of scrambling in April." },
  { icon: ShieldCheck, title: "Secure Document Intake", text: "Upload W-2s, 1099s and IDs through our encrypted client portal with e-signature." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-11 w-auto rounded-sm" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm" data-testid="nav-links">
            <Link to="/services" data-testid="nav-services-link" className="text-muted-foreground hover:text-foreground transition-colors">Services</Link>
            <a href="#office" data-testid="nav-office-link" className="text-muted-foreground hover:text-foreground transition-colors">Office</a>
            <a href="#locations" data-testid="nav-locations-link" className="text-muted-foreground hover:text-foreground transition-colors">Locations</a>
            <Link to="/team" data-testid="nav-team-link" className="text-muted-foreground hover:text-foreground transition-colors">Team</Link>
            <Link to="/book" data-testid="nav-book-link" className="text-muted-foreground hover:text-foreground transition-colors">Book Appointment</Link>
            <Link to="/support" data-testid="nav-support-link" className="text-muted-foreground hover:text-foreground transition-colors">Support</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/auth" data-testid="nav-login-button">Client Login</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/book" data-testid="nav-get-started-button">Book Now</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={COMPANY.images.hero} alt="Laronta Pro Tax office" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-40">
          <p className="text-xs tracking-[0.25em] uppercase text-secondary font-medium animate-fade-up">Taylorsville, Utah</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tighter text-white max-w-2xl leading-[1.05] animate-fade-up" style={{ animationDelay: "80ms" }}>
            Tax season, minus the stress.
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-xl animate-fade-up" style={{ animationDelay: "160ms" }}>
            Complete your intake online, sign digitally, upload your documents, and book an appointment — all before you ever step into our office.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <Link to="/auth" data-testid="hero-start-intake-button">
                Start Your Intake <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white bg-white/10 hover:bg-white/20 hover:text-white">
              <a href={COMPANY.phoneHref} data-testid="hero-call-button">
                <Phone className="w-4 h-4 mr-2" /> {COMPANY.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section id="services" className="max-w-7xl mx-auto px-6 py-24" data-testid="services-section">
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">What we do</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mt-3">
          <h2 className="md:col-span-7 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
            More than taxes — we build corporate tax strategy.
          </h2>
          <div className="md:col-span-5 md:justify-self-end">
            <Button asChild variant="outline">
              <Link to="/services" data-testid="view-all-services-button">Explore All Services <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <div key={s.title} data-testid={`service-card-${i}`}
              className="bg-card border border-border rounded-md p-8 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
              <s.icon className="w-8 h-8 text-primary" />
              <h3 className="mt-5 text-xl font-heading font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          <div className="md:col-span-7 relative rounded-md overflow-hidden min-h-[280px] border border-border">
            <img src="/presentation.png" alt="The Laronta Pro Tax Services team" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="md:col-span-5 bg-primary text-primary-foreground rounded-md p-10 flex flex-col justify-center">
            <h3 className="text-2xl font-heading font-semibold tracking-tight">A team that knows your name.</h3>
            <p className="mt-3 text-sm text-primary-foreground/80 leading-relaxed">
              We're a local Taylorsville practice. Corporate tax strategy, individual returns, multi-state filing, gift & estate planning, bookkeeping and payroll — when you book with us, you sit down with a real person who reviewed your file.
            </p>
            <Button asChild className="mt-6 w-fit bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <Link to="/auth" data-testid="services-book-button">Book an Appointment</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="office" className="bg-muted/50 border-y border-border" data-testid="office-section">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Visit us</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">Our office on Redwood Road.</h2>
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">{COMPANY.name}</p>
                  <p className="text-muted-foreground">{COMPANY.address}<br />{COMPANY.city}, {COMPANY.state} {COMPANY.zip}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary" />
                <a href={COMPANY.phoneHref} data-testid="office-phone-link" className="hover:text-primary transition-colors">{COMPANY.phone}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <a href={COMPANY.emailHref} data-testid="office-email-link" className="hover:text-primary transition-colors">{COMPANY.email}</a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-0.5" />
                <div className="text-muted-foreground">
                  {COMPANY.hours.map((h) => (
                    <p key={h.days}>{h.days}: {h.time}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7 rounded-md overflow-hidden border border-border">
            <img src={COMPANY.images.office} alt="Inside the Laronta Pro Tax office" className="w-full h-full object-cover min-h-[320px]" />
          </div>
        </div>
      </section>

      <section id="locations" className="max-w-7xl mx-auto px-6 py-24" data-testid="locations-section">
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Nationwide reach</p>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight max-w-2xl">
          We are where you are.
        </h2>
        <p className="mt-4 text-muted-foreground max-w-xl">
          With hubs across the country, Laronta Pro combines national reach with local expertise. Find your nearest office below.
        </p>
        <div className="mt-10" data-testid="states-map-wrapper">
          <USMap />
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Headquarters: {COMPANY.fullAddress} · <a href={COMPANY.phoneHref} className="text-primary hover:underline underline-offset-2">{COMPANY.phone}</a>
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 text-left" data-testid="cta-section">
        <div className="bg-primary rounded-md px-10 py-16 md:px-16 relative overflow-hidden">
          <h2 className="text-3xl lg:text-4xl font-heading font-semibold tracking-tight text-primary-foreground max-w-lg">
            Ready to get your refund moving?
          </h2>
          <p className="mt-3 text-primary-foreground/75 max-w-md text-sm">
            Create your free client account and finish your intake in about 10 minutes.
          </p>
          <Button asChild size="lg" className="mt-8 bg-secondary text-secondary-foreground hover:bg-secondary/90">
            <Link to="/auth" data-testid="cta-create-account-button">Create Account <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-border" data-testid="landing-footer">        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-6 text-sm text-muted-foreground">
          <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-9 w-auto rounded-sm" />
          <p>{COMPANY.fullAddress} · {COMPANY.phone}</p>
          <div className="flex gap-6">
            <Link to="/services" data-testid="footer-services-link" className="hover:text-foreground transition-colors">Services</Link>
            <Link to="/team" data-testid="footer-team-link" className="hover:text-foreground transition-colors">Team</Link>
            <Link to="/support" data-testid="footer-support-link" className="hover:text-foreground transition-colors">Support</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/portal" data-testid="footer-team-portal-link" className="hover:text-foreground transition-colors">Team Portal</Link>
          </div>
        </div>
      </footer>
      <AIChatWidget />
    </div>
  );
}
